import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import MenuBar from "@/components/MenuBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, Users, Calendar, Send, MessageSquare } from "lucide-react";
import { AMAService } from "@/services/amaService";
import { format } from "date-fns";
import { nb, enUS } from "date-fns/locale";

interface QuestionFormData {
  question_text: string;
  asker_name?: string;
}

const AMADetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = i18n.language === 'no' ? nb : enUS;

  const { data: ama } = useQuery({
    queryKey: ['ama', id],
    queryFn: () => AMAService.getAMAById(id!),
    enabled: !!id,
  });

  const { data: questions } = useQuery({
    queryKey: ['ama-questions', id],
    queryFn: () => AMAService.getQuestionsByAMA(id!),
    enabled: !!id,
  });

  const { data: answers } = useQuery({
    queryKey: ['ama-answers', id],
    queryFn: () => AMAService.getAnswersByAMA(id!),
    enabled: !!id,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<QuestionFormData>();

  const submitQuestionMutation = useMutation({
    mutationFn: (data: QuestionFormData) =>
      AMAService.submitQuestion({
        ...data,
        ama_id: id!,
      }),
    onSuccess: () => {
      toast.success(t('ama.public.questionSubmitted'));
      reset();
      queryClient.invalidateQueries({ queryKey: ['ama-questions', id] });
    },
    onError: () => {
      toast.error(t('ama.public.questionError'));
    },
  });

  const onSubmit = (data: QuestionFormData) => {
    submitQuestionMutation.mutate(data);
  };

  const getAnswer = (questionId: string) => {
    return answers?.find(a => a.question_id === questionId);
  };

  const approvedQuestions = questions?.filter(q => q.status === 'approved') || [];

  if (!ama) {
    return (
      <div className="min-h-screen bg-background">
        <MenuBar />
        <div className="container mx-auto px-4 py-8">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const isLive = ama.status === 'live';
  const isPlanned = ama.status === 'planned';

  return (
    <div className="min-h-screen bg-background">
      <MenuBar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/ama')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('ama.public.backToAMA')}
        </Button>

        {/* AMA Header */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {isLive && (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
                      </span>
                      {t('ama.status.live')}
                    </Badge>
                  )}
                  {isPlanned && (
                    <Badge variant="secondary">{t('ama.status.planned')}</Badge>
                  )}
                  {ama.status === 'archived' && (
                    <Badge variant="outline">{t('ama.status.archived')}</Badge>
                  )}
                </div>
                
                <CardTitle className="text-3xl mb-2">{ama.topic}</CardTitle>
                
                <div className="space-y-2 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">{ama.expert_name}</span>
                    {ama.expert_title && <span>• {ama.expert_title}</span>}
                    {ama.expert_company && <span>• {ama.expert_company}</span>}
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(ama.scheduled_at), 'PPPp', { locale })}
                  </div>
                </div>

                {ama.description && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-foreground mb-1">Kontekst</h3>
                    <p className="text-muted-foreground">{ama.description}</p>
                  </div>
                )}
              </div>

              {ama.expert_photo_url && (
                <img
                  src={ama.expert_photo_url}
                  alt={ama.expert_name}
                  className="w-24 h-24 rounded-full object-cover"
                />
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Question Form */}
        {(isLive || isPlanned) && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{t('ama.public.askQuestion')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="asker_name">{t('ama.public.yourName')}</Label>
                  <Input
                    id="asker_name"
                    {...register('asker_name')}
                    placeholder={t('ama.public.namePlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="question_text">
                    {t('ama.public.yourQuestion')} *
                  </Label>
                  <Textarea
                    id="question_text"
                    {...register('question_text', { required: true })}
                    placeholder={t('ama.public.questionPlaceholder')}
                    rows={4}
                  />
                  {errors.question_text && (
                    <p className="text-sm text-destructive">{t('common.required')}</p>
                  )}
                </div>

                <Button type="submit" disabled={submitQuestionMutation.isPending}>
                  <Send className="mr-2 h-4 w-4" />
                  {t('ama.public.submitQuestion')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Q&A List */}
        <div>
          <h2 className="text-2xl font-bold mb-4">
            {t('ama.public.questionsAndAnswers')}
          </h2>
          
          <div className="space-y-6">
            {approvedQuestions.map((question) => {
              const answer = getAnswer(question.id);
              
              return (
                <Card key={question.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-start gap-2 mb-2">
                          <MessageSquare className="h-5 w-5 text-primary mt-1" />
                          <div className="flex-1">
                            <p className="font-medium text-lg">{question.question_text}</p>
                            {question.asker_name && (
                              <p className="text-sm text-muted-foreground mt-1">
                                — {question.asker_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {answer && (
                        <>
                          <Separator />
                          <div className="bg-muted/50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-muted-foreground mb-2">
                              {ama.expert_name} {t('ama.public.answered')}:
                            </p>
                            <p className="whitespace-pre-wrap">{answer.answer_text}</p>
                          </div>
                        </>
                      )}

                      {!answer && isLive && (
                        <p className="text-sm text-muted-foreground italic">
                          {t('ama.public.waitingForAnswer')}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {approvedQuestions.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {t('ama.public.noQuestionsYet')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMADetailPage;
